import { useAntNotification } from 'components/ant/AntNotification';
import { useCallback, useState } from 'react';
import { useRfqsBulkSendOfferMessagesMutation } from 'utils/graphql/mutations/__generated__/rfqsBulkSendOfferMessages';
import useAsyncJobStatus from 'utils/hooks/useAsyncJobStatus';

type BulkJobKey = 'total_offers_count' | 'sent_offer_ids' | 'failed_offer_ids';

interface Progress {
  status: 'queued' | 'working' | 'complete' | 'retrying' | 'failed' | 'interrupted' | null;
  total: number;
  sentCount: number;
  failedCount: number;
  sentOfferIds: string[];
  failedOfferIds: string[];
}

const parseIds = (raw?: string): string[] => {
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);

    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // not JSON → fallback to CSV parsing
  }

  return raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
};

const parseCount = (raw?: string): number => {
  const num = Number(raw);

  return Number.isNaN(num) ? 0 : num;
};

export const useBulkSendOfferMessagesFromTemplate = () => {
  const notification = useAntNotification();
  const [progress, setProgress] = useState<Progress | null>(null);

  const [sendBulkMessages, { loading: mutationLoading }] = useRfqsBulkSendOfferMessagesMutation();

  const resetProgress = useCallback(() => setProgress(null), []);

  const [startPolling, jobStatusLoading] = useAsyncJobStatus<BulkJobKey>({
    keys: ['total_offers_count', 'sent_offer_ids', 'failed_offer_ids'],

    onProgress: (status, values) => {
      const total = parseCount(values.total_offers_count);
      const sentOfferIds = parseIds(values.sent_offer_ids);
      const failedOfferIds = parseIds(values.failed_offer_ids);

      setProgress({
        status,
        total,
        sentCount: sentOfferIds.length,
        failedCount: failedOfferIds.length,
        sentOfferIds,
        failedOfferIds,
      });
    },

    onCompleted: (terminalStatus, values) => {
      const total = parseCount(values.total_offers_count);
      const sentOfferIds = parseIds(values.sent_offer_ids);
      const failedOfferIds = parseIds(values.failed_offer_ids);

      setProgress({
        status: terminalStatus === 'complete' ? 'complete' : 'failed',
        total,
        sentCount: sentOfferIds.length,
        failedCount: failedOfferIds.length,
        sentOfferIds,
        failedOfferIds,
      });

      if (terminalStatus === 'complete') {
        if (failedOfferIds.length === 0) {
          notification.success({ message: `Messages sent: ${sentOfferIds.length}/${total}` });
        } else {
          notification.error({
            message: `Completed with errors. Sent ${sentOfferIds.length}/${total}, failed ${failedOfferIds.length}`,
          });
        }
      } else {
        notification.error({ message: 'Bulk message sending failed' });
      }
    },
  });

  const sendBulkOfferMessages = async ({
    rfqId,
    rfqOfferIds,
    templateGroupId,
    message,
  }: {
    rfqId: string;
    rfqOfferIds: string[];
    templateGroupId?: string;
    message?: string;
  }) => {
    if (!rfqId || (!templateGroupId && !message) || rfqOfferIds.length === 0) return;

    setProgress({
      status: 'queued',
      total: rfqOfferIds.length,
      sentCount: 0,
      failedCount: 0,
      sentOfferIds: [],
      failedOfferIds: [],
    });

    try {
      const { data } = await sendBulkMessages({
        variables: {
          input: {
            rfqId,
            rfqOfferIds,
            templateGroupId,
            message,
          },
        },
      });

      const jobId = data?.rfqsBulkSendOfferMessages?.jobId;

      if (!jobId) {
        setProgress((prev) => (prev ? { ...prev, status: 'failed' } : prev));
        notification.error({ message: 'Bulk send did not return jobId' });

        return;
      }

      void startPolling(jobId);
    } catch (e) {
      setProgress((prev) => (prev ? { ...prev, status: 'failed' } : prev));

      notification.error({ message: 'Bulk message sending failed. Please try again.' });
      console.error('rfqsBulkSendOfferMessages error', e);
    }
  };

  const loading = mutationLoading || jobStatusLoading;

  return {
    sendBulkOfferMessages,
    loading,
    progress,
    resetProgress,
  };
};
